import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, combineLatest, map, startWith, tap } from 'rxjs';
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { InventoryAttachmentStore } from 'src/app/stores/component/inventory-attachment-store';
import { InventoryStore } from 'src/app/stores/component/inventory-store';
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import * as CompanyActions from "../../stores/global/companies/company-actions";
import { companiesWithInventory } from '../helpers/companies-with-inventory';
import { canOverrideNextService, computeNextService, isNextServiceComputed } from '../helpers/next-service';
import { ICreateInventoryDeviceRequest, IInventoryDeviceDetails, IUpdateInventoryDeviceRequest } from '../models/inventory-device';

/**
 * Detail page of an inventory device (create with id 'new', otherwise edit).
 * Route data `adminView: true` marks the HTEC variant (company selectable).
 */
@UntilDestroy()
@Component({
    selector: 'app-inventory-detail',
    templateUrl: './inventory-detail.component.html',
    styleUrl: './inventory-detail.component.scss',
    providers: [
        InventoryStore,
        InventoryAttachmentStore
    ],
    standalone: false
})
export class InventoryDetailComponent implements OnInit {
  isAdminView = false;
  canEdit = false;
  isNew = false;

  /** True while the next service date is derived from the latest service + interval (field locked). */
  nextServiceComputed = false;
  private currentDevice: IInventoryDeviceDetails | null = null;
  private manualNextService: Date | null = null;
  listUrl: string;
  public deletionDialogOpened = false;

  deviceIdToDelete$ = new Subject<string>();

  deviceForm = new FormGroup({
    id: new UntypedFormControl(null),
    companyId: new UntypedFormControl(null, {
      validators: Validators.required,
      updateOn: 'change'
    }),
    serialNumber: new UntypedFormControl(null, {
      validators: Validators.required,
      updateOn: 'change'
    }),
    name: new UntypedFormControl(null, {
      validators: Validators.required,
      updateOn: 'change'
    }),
    manufacturer: new UntypedFormControl(null),
    purchaseDate: new UntypedFormControl(null),
    purchaseCost: new UntypedFormControl(null),
    serviceInterval: new UntypedFormControl(null),
    active: new UntypedFormControl(true),
    location: new UntypedFormControl(null),
    remarks: new UntypedFormControl(null),
    serviceInformation: new UntypedFormControl(null),
    nextService: new UntypedFormControl(null),
    nextServiceOverridden: new UntypedFormControl(false),
  });

  vm$: Observable<any>;

  constructor(
    private store: Store<AppStateInterface>,
    private route: ActivatedRoute,
    private router: Router,
    private inventoryStore: InventoryStore,
    private permissionService: PermissionStorageService,
  ) {
    this.isAdminView = this.route.snapshot.data['adminView'] === true;
    this.listUrl = this.isAdminView ? '/inventory-admin' : '/inventory';

    this.vm$ = combineLatest([
      this.inventoryStore.device$.pipe(
        tap(device => this.patchForm(device))
      ),
      this.inventoryStore.loading$,
      this.store.pipe(select(companySelector)),
      this.store.pipe(select(currentEmployeeSelector)).pipe(
        tap(employee => {
          // customers always create inventory devices for their own company
          if (!this.isAdminView && this.isNew && employee?.company?.id) {
            this.deviceForm.get('companyId').setValue(employee.company.id);
          }
        })
      ),
      this.deviceIdToDelete$.pipe(startWith(null))
    ]).pipe(
      map(([
        device,
        loading,
        companies,
        currentEmployee,
        deviceIdToDelete
      ]) => ({
        device,
        loading,
        // customer dropdown: only companies with the inventory extension enabled (plus the device's own company)
        companies: companiesWithInventory(companies, device?.companyId),
        currentEmployee,
        deviceIdToDelete
      }))
    );
  }

  ngOnInit(): void {
    this.checkPermissions();

    // Live preview of the backend rule: entering an interval while services exist locks the field and shows
    // the computed date; removing the interval restores the manually entered date.
    this.deviceForm.get('serviceInterval').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.applyNextServiceRule());

    this.deviceForm.get('nextService').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (!this.nextServiceComputed) {
          this.manualNextService = value;
        }
      });

    // the same component instance is reused when navigating from 'new' to the created id
    this.route.paramMap
      .pipe(untilDestroyed(this))
      .subscribe(params => {
        const deviceId = params.get('id');
        this.isNew = deviceId == 'new';

        if (!this.isNew) {
          this.inventoryStore.getInventoryDeviceById(deviceId);
        }
      });

    // the current employee (with its company) is loaded by the AppComponent and guaranteed by the AuthGuard for customer routes
    if (this.isAdminView) {
      this.store.dispatch(CompanyActions.getCompanies());
    }
  }

  checkPermissions(): void {
    const permissions = this.permissionService.getPermissions();
    this.canEdit = permissions?.includes('Permissions.InventoryDevices.Update') === true;

    if (!this.canEdit) {
      this.deviceForm.disable();
    }
  }

  private patchForm(device: IInventoryDeviceDetails | null): void {
    if (!device) {
      return;
    }

    this.currentDevice = device;
    const nextService = device.nextService ? new Date(device.nextService) : null;

    this.deviceForm.patchValue({
      ...device,
      companyId: device.company?.id ?? device.companyId,
      purchaseDate: device.purchaseDate ? new Date(device.purchaseDate) : null,
      nextService,
      nextServiceOverridden: device.nextServiceOverridden === true,
    }, { emitEvent: false });

    // Remember the stored value as the manual one unless it is computed (then there is no manual value to restore)
    this.manualNextService = isNextServiceComputed(device.services, device.serviceInterval, device.nextServiceOverridden) ? null : nextService;
    this.applyNextServiceRule();

    this.deviceForm.markAsPristine();
  }

  /**
   * Mirrors InventoryDevice.IsNextServiceComputed: with an interval and existing services the date is computed
   * and the field is locked, otherwise it is entered manually (as long as the user may edit at all).
   * A manual override keeps the field editable although the base rule applies.
   */
  private applyNextServiceRule(): void {
    const services = this.currentDevice?.services ?? [];
    const interval = this.deviceForm.get('serviceInterval').value;
    const control = this.deviceForm.get('nextService');
    const overriddenControl = this.deviceForm.get('nextServiceOverridden');

    // an override is only meaningful while the date would otherwise be computed (same normalization as the backend)
    if (overriddenControl.value === true && !canOverrideNextService(services, interval)) {
      overriddenControl.setValue(false, { emitEvent: false });
    }

    const overridden = overriddenControl.value === true;
    const computed = isNextServiceComputed(services, interval, overridden);

    if (computed) {
      control.setValue(computeNextService(services, interval), { emitEvent: false });
      control.disable({ emitEvent: false });
    } else {
      if (this.nextServiceComputed) {
        control.setValue(this.manualNextService, { emitEvent: false });
      }

      if (this.canEdit) {
        control.enable({ emitEvent: false });
      }
    }

    // overriding without a date would clear the planned service (the backend rejects that as well)
    control.setValidators(overridden ? Validators.required : null);
    control.updateValueAndValidity({ emitEvent: false });

    this.nextServiceComputed = computed;
  }

  /** True while the stored/computed date is manually overridden (field editable although the base rule applies). */
  get nextServiceOverridden(): boolean {
    return this.deviceForm.get('nextServiceOverridden').value === true;
  }

  /** The override link is offered only while the date is computed and the user may edit. */
  get nextServiceOverrideAvailable(): boolean {
    return this.canEdit && canOverrideNextService(this.currentDevice?.services, this.deviceForm.get('serviceInterval').value);
  }

  overrideNextService(): void {
    // start from the computed date so the user only has to adjust it
    this.manualNextService = this.deviceForm.get('nextService').value;
    this.deviceForm.get('nextServiceOverridden').setValue(true, { emitEvent: false });
    this.applyNextServiceRule();
    this.deviceForm.markAsDirty();
  }

  resetNextServiceToComputed(): void {
    this.deviceForm.get('nextServiceOverridden').setValue(false, { emitEvent: false });
    this.applyNextServiceRule();
    this.deviceForm.markAsDirty();
  }

  /** Current value of the active flag, also when the form is disabled (read-only view). */
  get isActive(): boolean {
    return this.deviceForm.get('active').value === true;
  }

  toggleActive(): void {
    const activeControl = this.deviceForm.get('active');
    activeControl.setValue(!this.isActive);
    activeControl.markAsDirty();
    this.deviceForm.markAsDirty();
  }

  saveChanges(deviceId: string) {
    const formValues = this.deviceForm.getRawValue();

    const fields = {
      serialNumber: formValues.serialNumber,
      name: formValues.name,
      manufacturer: formValues.manufacturer,
      purchaseDate: formValues.purchaseDate,
      purchaseCost: formValues.purchaseCost,
      serviceInterval: formValues.serviceInterval,
      active: formValues.active === true,
      location: formValues.location,
      remarks: formValues.remarks,
      serviceInformation: formValues.serviceInformation,
      nextService: formValues.nextService,
    };

    if (deviceId) {
      const updateRequest: IUpdateInventoryDeviceRequest = {
        id: deviceId,
        ...fields,
        nextServiceOverridden: formValues.nextServiceOverridden === true
      };

      this.inventoryStore.updateInventoryDevice(updateRequest);
    } else {
      const createRequest: ICreateInventoryDeviceRequest = {
        companyId: formValues.companyId,
        ...fields
      };

      this.inventoryStore.createInventoryDevice({
        request: createRequest,
        onSuccess: (device) => this.router.navigate([this.listUrl, device.id])
      });
    }

    this.deviceForm.markAsPristine();
  }

  delete(deviceId: string) {
    if (deviceId) {
      this.inventoryStore.deleteInventoryDevice({
        id: deviceId,
        onSuccess: () => this.router.navigate([this.listUrl])
      });
    }
  }

  // delete confirmation dialog
  public deletionDialogClose(status: string, deviceId: string): void {
    this.deletionDialogOpened = false;

    if (status == 'delete') {
      this.delete(deviceId);
    }

    this.deviceIdToDelete$.next(null);
  }

  public deletionDialogOpen(deviceId: string): void {
    this.deletionDialogOpened = true;
    this.deviceIdToDelete$.next(deviceId);
  }
}